import { calculateProportionalDiscount } from "./calculate-proportional-discount";
import { resolveDiscountAmount } from "./resolve-discount-amount";

type AllowanceLine = {
  basketId: string;
  totalPrice: number;
  quantity: number;
};

type AllowanceDiscount = {
  amount: number;
  amountType: "currency" | "percentage";
};

/**
 * Shared `unitPrice` allowance rule: resolve the discount, cap at the remaining
 * budget, distribute proportionally over eligible lines. POS
 * (`computeProductsInBasket`) and kiosk (`applyUnitPriceAllowances`) both drive
 * off this for identical totals.
 *
 * Note: the server splits a capped allowance sequentially, not proportionally,
 * so the order total matches but the per-line split may differ.
 *
 * @param lines eligible lines — post-other-discount totals (incl VAT) + quantities
 * @param remaining remaining allowance budget (incl VAT)
 * @param discount backing discount definition
 * @returns deduction per line, keyed by `basketId`
 */
export function distributeUnitPriceAllowance({
  lines,
  remaining,
  discount
}: {
  lines: AllowanceLine[];
  remaining: number;
  discount: AllowanceDiscount;
}): { basketId: string; amount: number }[] {
  // Only lines still costing something absorb a deduction — a free/fully-discounted
  // line (totalPrice 0) must not catch the rounding residual.
  const eligible = lines.filter((line) => line.totalPrice > 0);
  if (!eligible.length) return [];

  const eligibleTotal = eligible.reduce(
    (acc, line) => acc + line.totalPrice,
    0
  );
  const eligibleQuantity = eligible.reduce(
    (acc, line) => acc + line.quantity,
    0
  );

  const calculatedDiscount = resolveDiscountAmount(
    discount,
    eligibleTotal,
    eligibleQuantity
  );
  // Cap at eligible line value (a per-unit currency allowance × qty can exceed
  // it) and at the remaining budget.
  const totalDiscount = Math.min(remaining, calculatedDiscount, eligibleTotal);

  return calculateProportionalDiscount(eligible, eligibleTotal, totalDiscount);
}
